import { exactInteger } from '@/lib/exact'
import type { Block, Cell, ChipCell, Column, FormBlock, TableBlock } from '@/kova/spec/types'

import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'

import type { Caller } from './handles'
import type { ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'
import { callerBeside, submitWrite, writeRefusal } from './writes'

/**
 * Content & Localization › String catalogs (`ct-strings`) and Translation
 * backlog (`ct-backlog`), read from `GET /v1/admin/copy/languages` and
 * `GET /v1/admin/copy/backlog/{language}` (go-wallet#155/#156, handlers
 * `internal/server/admin_copy.go` and `admin_copy_backlog.go`).
 *
 * # Two numbers, and they are not the same kind of number
 *
 * This is the distinction the whole section turns on, and the endpoint states
 * it first:
 *
 *   > `missing` is EXACT — the set difference between the keys the running
 *   > image carries and the live translations in the database. `observations`,
 *   > on the backlog screen, is SAMPLED from serving pods and lost on restart;
 *   > it orders the work and never defines it.
 *
 * So a count comes from `missing` and an order comes from `observations`, and
 * neither is ever used for the other's job. A backlog counter bound to the page
 * length would report "how many are on this page" as "how much work is left".
 *
 * # A zero observation means NOT OBSERVED, never TRANSLATED
 *
 * The row's presence is what says the translation is missing. A restart loses
 * samples and a standby cell contributes none, so a key sitting at zero is one
 * nobody has been recorded hitting — not one nobody needs.
 *
 * # `translation.available: false` is a sentence, never a 0 and never a 100%
 *
 * It means the backlog cannot be COMPUTED — a fresh database, or a deployment
 * whose reporter has never run. Rendering a zero in its place would claim a
 * finished job; rendering 100% would claim the same thing louder. The reason
 * names which emitter has not reported, and the reason is what this screen
 * shows.
 *
 * # A key the registry holds and no deployed image carries is not work
 *
 * It cannot be translated into completeness and it never stops being asked for,
 * so it is counted BESIDE the coverage as `registry_keys_not_in_build` and left
 * off the working list. Counting it against a translator would make the
 * percentage one nobody can finish.
 *
 * # Read-only
 *
 * `POST /copy/languages/{language}` enables or withdraws a language, which
 * changes what every customer can select and what the apps offer. It is not
 * called here: that is a change to what customers are told, attributed with an
 * operator and a reason, and it needs the surface law 21 requires.
 */

export const COPY_LANGUAGES_PATH = '/copy/languages'

export function copyBacklogPath(language: string): string {
  return `/copy/backlog/${encodeURIComponent(language)}`
}

const LANGUAGES_SOURCE = 'postgres · copy_languages + copy_translations (GET /v1/admin/copy/languages)'
const BACKLOG_SOURCE = 'postgres · copy registry, ordered by sampled demand (GET /v1/admin/copy/backlog/{language})'
const AUTHORITY = 'operational · the catalog the apps serve'

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function whole(value: unknown): number | null {
  return exactInteger(value)
}

function bool(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

// ---------------------------------------------------------------------------
// ct-strings — the languages and their coverage
// ---------------------------------------------------------------------------

interface Language {
  readonly language: string
  readonly endonym: string
  readonly enabled: boolean | null
  readonly isBase: boolean | null
  readonly keysInBuild: number | null
  /** False means coverage CANNOT BE COMPUTED — never zero, never 100%. */
  readonly coverageAvailable: boolean | null
  readonly coverageReason: string
  readonly applicable: boolean | null
  readonly keysRegistered: number | null
  readonly missing: number | null
  readonly translated: number | null
  readonly percent: number | null
  readonly registryKeysNotInBuild: number | null
  readonly registryMatchesBuild: boolean | null
  readonly changedBy: string
  readonly reason: string
}

function readLanguage(raw: unknown): Language | null {
  if (!isObject(raw)) return null
  const language = text(raw['language'])
  if (language === '') return null
  const t = isObject(raw['translation']) ? raw['translation'] : {}
  return {
    language,
    endonym: text(raw['endonym']),
    enabled: bool(raw['enabled']),
    isBase: bool(raw['is_base']),
    keysInBuild: whole(raw['keys_in_build']),
    coverageAvailable: bool(t['available']),
    coverageReason: text(t['reason']),
    applicable: bool(t['applicable']),
    keysRegistered: whole(t['keys_registered']),
    missing: whole(t['missing']),
    translated: whole(t['translated']),
    percent: whole(t['translated_percent']),
    registryKeysNotInBuild: whole(t['registry_keys_not_in_build']),
    registryMatchesBuild: bool(t['registry_matches_build']),
    changedBy: text(raw['changed_by']),
    reason: text(raw['reason']),
  }
}

/**
 * The coverage cell.
 *
 * Three readings and only one of them is a number. `available: false` renders
 * the reason — a zero there claims a finished job and a 100% claims it louder —
 * and the base language renders as having no backlog by construction rather
 * than as complete, because "complete" implies work that was done.
 */
function coverageCell(l: Language): ChipCell {
  if (l.coverageAvailable === false) {
    return [l.coverageReason === '' ? 'cannot be computed' : `cannot be computed — ${l.coverageReason}`, 'a']
  }
  if (l.coverageAvailable === null) return ['— not stated', 'r']
  if (l.applicable === false) return ['no backlog by construction', 'n']
  if (l.percent === null) return ['— no percentage returned', 'a']
  return [`${String(l.percent)}%`, l.percent >= 100 ? 'g' : l.percent >= 80 ? 'a' : 'r']
}

const LANG_COLS: readonly Column[] = [
  ['Language', 'm', '110px'],
  ['Name', 'x', 'minmax(150px,1.1fr)'],
  ['Offered to customers', 'c', '170px'],
  ['Keys in the build', 'n', '130px'],
  ['Missing (exact)', 'n', '130px'],
  ['Coverage', 'c', 'minmax(180px,1.3fr)'],
  ['Changed by', 'x', '150px'],
]

function langRow(l: Language): readonly Cell[] {
  return [
    l.language,
    l.endonym === '' ? '— no endonym' : l.endonym,
    l.isBase === true
      ? (['base — cannot be disabled', 'n'] as ChipCell)
      : l.enabled === null
        ? (['— not stated', 'r'] as ChipCell)
        : l.enabled
          ? (['offered', 'g'] as ChipCell)
          : (['not offered', 'n'] as ChipCell),
    l.keysInBuild === null ? '—' : String(l.keysInBuild),
    // EXACT, and the same number the backlog screen reports as `missing`.
    l.coverageAvailable === false ? '—' : l.missing === null ? '—' : String(l.missing),
    coverageCell(l),
    l.changedBy === '' ? '— never changed' : l.changedBy,
  ]
}

function driftNote(rows: readonly Language[]): Block | null {
  const drifted = rows.filter((l) => l.registryMatchesBuild === false || (l.registryKeysNotInBuild ?? 0) > 0)
  if (drifted.length === 0) return null
  return {
    t: 'note',
    tone: 'n',
    quiet: true,
      kicker: 'Not translation work',
    title: 'The registry holds keys this build does not carry',
    body: 'An image no longer deployed, or one not deployed yet mid-rollout. Those keys cannot be translated into completeness and they never stop being asked for, so they are reported beside the coverage rather than inside it — a percentage nobody can finish is one nobody can act on, and counting them against a translator would be exactly that.',
    ref: drifted
      .map((l) => `${l.language}: ${String(l.registryKeysNotInBuild ?? 0)} registry key(s) not in this build`)
      .join(' · '),
  }
}


export function composeLanguages(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const raw = Array.isArray(payload['languages']) ? (payload['languages'] as readonly unknown[]) : []
  const rows: Language[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readLanguage(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const base = text(payload['base_language'])
  const uncomputable = rows.filter((l) => l.coverageAvailable === false)

  const table: TableBlock = {
    t: 'table',
    title: 'copy_languages · coverage per language',
    src: 'ledger',
    facets: false,
    cols: LANG_COLS,
    rows: rows.map(langRow),
    footer: `${String(rows.length)} configured language(s)${uncomputable.length === 0 ? '' : ` · ${String(uncomputable.length)} whose coverage cannot be computed`}${unreadable === 0 ? '' : ` · ${String(unreadable)} could not be read`}`,
    foot2:
      'The missing count is EXACT — the set difference between the keys the running image carries and the live translations in the database. It is not the sampled figure that orders the backlog, and the two are never used for each other’s job.',
  }

  const blocks: Block[] = [table]

  if (uncomputable.length > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Not a finished job',
      title: `Coverage cannot be computed for ${String(uncomputable.length)} language(s)`,
      body: 'That is not "nothing to translate". It appears when no process has reported its catalog key set — a fresh database, or a deployment where the reporter has never run — and the honest rendering is the reason rather than a zero. A zero would claim a finished job, and a 100% would claim it louder.',
      ref: uncomputable
        .map((l) => `${l.language}: ${l.coverageReason === '' ? 'no reason given' : l.coverageReason}`)
        .join(' · '),
    })
  }

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'String catalogs',
      title: 'No language is configured at all',
      body: 'Exactly one language is the base — the language the catalog is embedded in and the last rung of every fallback chain — and it cannot be disabled. A list with nothing in it has no base either, which means every string would fall through to its key, and the app forbids rendering a key.',
      ref: 'copy_languages · the read returned no rows',
    })
  }


  const drift = driftNote(rows)
  if (drift !== null) blocks.push(drift)
  
  const missing: string[] = []
  if (base === '') missing.push('which language is the base')
  for (const l of uncomputable) missing.push(`coverage for ${l.language}`)

  return {
    blocks,
    provenance:
      missing.length === 0
        ? { kind: 'live', source: LANGUAGES_SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) }
        : { kind: 'partial', source: LANGUAGES_SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt), missing },
    rowKeys: rows.map((l) => l.language),
  }
}

// ---------------------------------------------------------------------------
// ct-backlog — the working list for one language
// ---------------------------------------------------------------------------

interface BacklogEntry {
  readonly key: string
  readonly base: string
  readonly knownToBuild: boolean | null
  /** SAMPLED. A zero means NOT OBSERVED, never "nobody needs this". */
  readonly observations: number | null
}

function readEntry(raw: unknown): BacklogEntry | null {
  if (!isObject(raw)) return null
  const key = text(raw['key'])
  if (key === '') return null
  return {
    key,
    base: text(raw['base']),
    knownToBuild: bool(raw['known_to_build']),
    observations: whole(raw['observations']),
  }
}

const BACKLOG_COLS: readonly Column[] = [
  ['Key', 'm', 'minmax(220px,1.5fr)'],
  ['The sentence to translate', 'x', 'minmax(260px,2fr)'],
  ['Times a fallback was observed', 'n', '210px'],
]

function backlogRow(e: BacklogEntry): readonly Cell[] {
  return [
    e.key,
    e.base === '' ? '— no base sentence' : e.base,
    // A zero is NOT OBSERVED. The row's presence is what says the translation
    // is missing, so a zero here never means "translated" or "not needed".
    e.observations === null ? '— not sampled' : e.observations === 0 ? '0 — not observed' : String(e.observations),
  ]
}


/**
 * The other half of the backlog: what is written and awaiting a human.
 *
 * The design's own copy for this screen is "every missing string AND every
 * legal string awaiting professional review". The missing half comes from the
 * backlog read; this is the second, and leaving it out made the screen answer
 * only the easier question.
 *
 * # `review_state` and `live` are different facts
 *
 * `live` is "published AND unclosed"; `review_state` stays what it was. A
 * translation can be published and later closed by a successor, and its review
 * state does not rewind — so a console reading one for the other would report a
 * superseded translation as awaiting review, or a rejected one as serving.
 *
 * # `legal` changes who may translate it
 *
 * Contractual text is "professional translation only, and a sign-off from
 * somebody who is not the translator". A legal string in review is not the same
 * queue item as a marketing one, and it is marked so nobody clears it casually.
 */
const REVIEW_COLS: readonly Column[] = [
  ['Key', 'm', 'minmax(200px,1.4fr)'],
  ['Version', 'm', '90px'],
  ['Contractual', 'c', '150px'],
  ['Review state', 'c', '150px'],
  ['Serving now', 'c', '140px'],
  ['Written by', 'x', 'minmax(150px,1.1fr)'],
]

function reviewRow(raw: unknown): readonly Cell[] | null {
  if (!isObject(raw)) return null
  const key = text(raw['copy_key'])
  if (key === '') return null
  const state = text(raw['review_state'])
  const legal = typeof raw['legal'] === 'boolean' ? raw['legal'] : null
  const live = typeof raw['live'] === 'boolean' ? raw['live'] : null
  const kind = text(raw['author_kind'])
  return [
    key,
    whole(raw['version']) === null ? '—' : `v${String(whole(raw['version']))}`,
    legal === null
      ? (['— not stated', 'r'] as ChipCell)
      : legal
        ? (['CONTRACTUAL — professional only', 'r'] as ChipCell)
        : (['informational', 'n'] as ChipCell),
    state === 'in_review'
      ? (['awaiting a human', 'a'] as ChipCell)
      : state === 'published'
        ? (['published', 'g'] as ChipCell)
        : state === 'rejected'
          ? (['rejected', 'r'] as ChipCell)
          : state === 'draft'
            ? (['draft', 'n'] as ChipCell)
            : ([state === '' ? '— not stated' : state, 'r'] as ChipCell),
    // Distinct from review_state: published-and-since-closed is not "not
    // published", and a console reading one for the other would report a
    // superseded translation as awaiting review.
    live === null
      ? (['— not stated', 'r'] as ChipCell)
      : live
        ? (['serving', 'g'] as ChipCell)
        : (['not serving', 'n'] as ChipCell),
    `${text(raw['authored_by']) === '' ? '— unattributed' : text(raw['authored_by'])}${kind === '' ? '' : ` · ${kind}`}`,
  ]
}

async function reviewQueue(api: ApiClient, language: string): Promise<readonly unknown[] | null> {
  try {
    const payload = await api.get<unknown>(`/copy/translations/${encodeURIComponent(language)}`)
    if (!isObject(payload)) return null
    const list = payload['workflow']
    return Array.isArray(list) ? (list as readonly unknown[]) : null
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return null
  }
}

export function composeBacklog(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const language = text(payload['language'])
  const missing = whole(payload['missing'])
  const returned = whole(payload['returned'])
  const limit = whole(payload['limit'])

  const raw = Array.isArray(payload['entries']) ? (payload['entries'] as readonly unknown[]) : []
  const rows: BacklogEntry[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readEntry(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const partial = missing !== null && returned !== null && missing > returned

  const table: TableBlock = {
    t: 'table',
    title: `Untranslated in ${language === '' ? 'this language' : language} · most-read first`,
    src: 'ledger',
    facets: false,
    cols: BACKLOG_COLS,
    // Never re-sorted. The order is observed demand, which is the backend's
    // statement about which string is costing the most fallbacks.
    rows: rows.map(backlogRow),
    footer: partial
      ? `${String(returned ?? rows.length)} shown of ${String(missing ?? 0)} missing — THIS IS A PAGE`
      : `${String(rows.length)} key(s) missing${unreadable === 0 ? '' : ` · ${String(unreadable)} could not be read`}`,
    foot2:
      'The order is by OBSERVED demand, which is sampled: counts accumulate in serving pods’ memory and are lost on restart, and a standby cell contributes none. A zero means not observed, never translated — the row being here is what says the translation is missing.',
  }

  const blocks: Block[] = [table]

  if (partial) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'A page, not the list',
      title: `${String(missing ?? 0)} keys are missing and ${String(returned ?? rows.length)} are shown`,
      body: 'Those are two different numbers and two different fields. A counter bound to the page length would report how much of the work fits on a screen as how much work there is — and the exact figure is the one the language screen shows.',
      ref: limit === null ? 'the page size was not stated' : `page size applied: ${String(limit)}`,
    })
  }

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'g',
      kicker: 'Backlog',
      title: 'Every key in the running build has a live translation in this language',
      body: 'Which is the state the apps assume: no string falls back, and no customer sees a sentence in a language they did not choose. Keys the registry holds that this build does not carry are not counted here — they cannot be translated into completeness.',
      ref: `${language === '' ? 'this language' : language} · missing = 0`,
    })
  }

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Partial read',
      title: `${String(unreadable)} backlog row(s) could not be read`,
      body: 'Counted rather than dropped. A working list shorter than the record is a string that stays untranslated because nobody saw it on the queue.',
      ref: 'an entry with no key names no string and is refused',
    })
  }

  const notInBuild = rows.filter((e) => e.knownToBuild === false)
  if (notInBuild.length > 0) {
    blocks.push({
      t: 'note',
      tone: 'n',
      kicker: 'Not this build',
      title: `${String(notInBuild.length)} key(s) here are not carried by the running build`,
      body: 'They are on the list and marked rather than hidden: the working list is the running build’s keys, and anything else is reported beside the coverage instead of inside it.',
      ref: notInBuild.map((e) => e.key).join(' · '),
    })
  }

  return {
    blocks,
    provenance: {
      kind: 'partial',
      source: BACKLOG_SOURCE,
      authority: AUTHORITY,
      readAt: readAtStamp(readAt),
      missing: [
        'how often each key was really hit — the ordering is sampled from serving pods and lost on restart',
      ],
    },
    rowKeys: rows.map((e) => e.key),
  }
}

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

function unreadableScreen(what: string, source: string, reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: what,
      title: `${what} could not be read`,
      body: `${reason} No language, no coverage and no missing key below is known — which is different from everything being translated, and different again from there being nothing to translate.`,
      ref: decisionRef === null ? 'Nothing was read.' : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source, reason },
  }
}

export const loadCopyLanguages: ScreenLoader = async (api) => {
  let payload: unknown
  const who = callerBeside(api)
  try {
    payload = await api.get<unknown>(COPY_LANGUAGES_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        'String catalogs',
        LANGUAGES_SOURCE,
        `The languages endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('String catalogs', LANGUAGES_SOURCE, 'The backend could not be reached.', null)
  }
  if (!isObject(payload)) {
    return unreadableScreen('String catalogs', LANGUAGES_SOURCE, 'The languages endpoint returned a shape this console does not know.', null)
  }
  const screen = composeLanguages(payload, new Date())
  return {
    ...screen,
    blocks: [...screen.blocks, languageForm(api, await who, languagesIn(payload))],
  }
}

/**
 * The backlog for the language with the most missing keys.
 *
 * The endpoint is per-language and the screen is one screen, so something has
 * to choose. It reads the language list first and picks the language with the
 * largest EXACT missing count — the most work — rather than the first
 * configured one, which would put a translator in front of whichever language
 * happens to sort first. A language whose coverage cannot be computed is not
 * eligible: it has no count to be the largest.
 */
export const loadCopyBacklog: ScreenLoader = async (api: ApiClient) => {
  let languages: unknown
  const who = callerBeside(api)
  try {
    languages = await api.get<unknown>(COPY_LANGUAGES_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        'Translation backlog',
        BACKLOG_SOURCE,
        `The languages endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`} and the backlog is read per language.`,
        error.decisionRef,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('Translation backlog', BACKLOG_SOURCE, 'The backend could not be reached.', null)
  }

  const rows: Language[] = []
  if (isObject(languages) && Array.isArray(languages['languages'])) {
    for (const entry of languages['languages'] as readonly unknown[]) {
      const parsed = readLanguage(entry)
      if (parsed !== null) rows.push(parsed)
    }
  }

  const eligible = rows.filter(
    (l) => l.coverageAvailable !== false && l.applicable !== false && (l.missing ?? 0) > 0,
  )
  const worst = eligible.reduce<Language | null>(
    (max, l) => (max === null || (l.missing ?? 0) > (max.missing ?? 0) ? l : max),
    null,
  )

  if (worst === null) {
    return {
      blocks: [{
        t: 'note',
        tone: 'g',
        kicker: 'Translation backlog',
        title: 'No configured language has a computable backlog with work in it',
        body: 'Either every language is fully translated against the running build, or the languages that are not have coverage that cannot be computed — which the String catalogs screen states per language, with the reason. Nothing is picked here rather than picking a language with no count to show.',
        ref: `${String(rows.length)} configured language(s) read`,
      }],
      provenance: {
        kind: 'partial',
        source: BACKLOG_SOURCE,
        authority: AUTHORITY,
        readAt: readAtStamp(new Date()),
        missing: ['a language to show a backlog for'],
      },
    }
  }

  let payload: unknown
  try {
    payload = await api.get<unknown>(copyBacklogPath(worst.language))
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        'Translation backlog',
        BACKLOG_SOURCE,
        `The backlog endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`} for ${worst.language}.`,
        error.decisionRef,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('Translation backlog', BACKLOG_SOURCE, 'The backend could not be reached.', null)
  }
  if (!isObject(payload)) {
    return unreadableScreen('Translation backlog', BACKLOG_SOURCE, 'The backlog endpoint returned a shape this console does not know.', null)
  }
  const data = composeBacklog(payload, new Date())

  // The other half of this screen. Read after the backlog and failing
  // independently: a translator who can see what is missing should still see it
  // when the review queue does not load.
  const workflow = await reviewQueue(api, worst.language)
  if (workflow === null) {
    return {
      ...data,
      provenance: {
        kind: 'partial',
        source: BACKLOG_SOURCE,
        authority: AUTHORITY,
        readAt: readAtStamp(new Date()),
        missing: [
          'how often each key was really hit — the ordering is sampled from serving pods and lost on restart',
          `what is written and awaiting review in ${worst.language}`,
        ],
      },
    }
  }

  const reviewRows: (readonly Cell[])[] = []
  let unreadableReviews = 0
  for (const entry of workflow) {
    const parsed = reviewRow(entry)
    if (parsed === null) unreadableReviews += 1
    else reviewRows.push(parsed)
  }

  const blocks: Block[] = [...data.blocks]
  if (reviewRows.length > 0 || unreadableReviews > 0) {
    blocks.push({
      t: 'table',
      title: `Written and awaiting a human — ${worst.language}`,
      src: 'ledger',
      facets: false,
      cols: REVIEW_COLS,
      rows: reviewRows,
      footer: `${String(reviewRows.length)} translation(s) in the workflow${unreadableReviews === 0 ? '' : ` · ${String(unreadableReviews)} could not be read`}`,
      foot2:
        'A contractual string is professional translation only, and needs a sign-off from somebody who is not the translator — it is not the same queue item as an informational one. “Serving now” is a different fact from the review state: a translation can be published and later closed by a successor, and its review state does not rewind.',
    })
  } else {
    blocks.push({
      t: 'note',
      tone: 'g',
      kicker: 'Nothing in review',
      title: `Nothing is written and waiting for a human in ${worst.language}`,
      body: 'The missing list above is therefore the whole of the work: nothing is drafted, in review, or rejected and waiting to be redone.',
      ref: `${worst.language} · the workflow is empty`,
    })
  }

  const caller = await who
  blocks.push(draftTranslationForm(api, caller, worst.language))
  blocks.push(stepTranslationForm(api, caller, translationsIn(workflow), 'submit'))
  blocks.push(stepTranslationForm(api, caller, translationsIn(workflow), 'decide'))

  return { ...data, blocks }
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/**
 * Offering a language to customers, or withdrawing one.
 *
 * # It does not check coverage, and neither does the backend
 *
 * An operator may offer a language with an untouched backlog: the fallback
 * chain covers every missing string and every response says so. The backend
 * declines to impose a threshold on purpose — "the alternative is a threshold
 * this service invents and then has to defend" — and leaves the judgement to a
 * person, with the coverage shown next to the switch. The table above this
 * control is that coverage.
 *
 * # `enabled` is required, and absent is not `false`
 *
 * The backend takes a POINTER so an omitted field is refused rather than read
 * as `false` — an omitted key would otherwise WITHDRAW a language, which is the
 * most destructive reading of a missing field on this surface. The control
 * matches that: the choice is a two-option select with nothing pre-selected,
 * not a checkbox whose unticked state is an answer nobody gave.
 */
function languageForm(api: ApiClient, caller: Caller, offered: readonly string[]): FormBlock {
  const blocked = writeRefusal(
    caller, 'copy.write', 'Ops', 'Offering or withdrawing a language',
  )
  return {
    t: 'form',
    title: 'Offer or withdraw a language',
    lead: 'Changes what every customer can select and what the apps offer. Coverage is not checked — a language with an untouched backlog falls back string by string, and every response says so. The table above is the coverage; the decision is yours.',
    fields: [
      {
        name: 'language', label: 'Language', kind: offered.length === 0 ? 'text' : 'select',
        required: true, mono: true,
        ...(offered.length === 0
          ? { placeholder: 'de', note: 'A BCP-47 tag.' }
          : { options: offered.map((l) => [l, l] as const) }),
      },
      {
        name: 'enabled', label: 'Offered to customers', kind: 'select', required: true,
        options: [
          ['true', 'offer — customers may select it'],
          ['false', 'withdraw — customers may no longer select it'],
        ],
        note: 'Required, and there is no default. An omitted answer would read as a withdrawal.',
      },
    ],
    reasons: [
      ['coverage_reached', 'The translation backlog is complete enough', 'true'],
      ['market_launch', 'Launching in a market that speaks it', 'true'],
      ['legal_required', 'Required by law in a market we serve', 'true'],
      ['coverage_lost', 'Coverage fell too far to keep offering it', 'false'],
      ['market_exit', 'Leaving the market that used it', 'false'],
    ],
    reasonNote: 'Goes on the language row and into the audit log in one transaction.',
    auditLine: 'admin.copy.language.set · actor · copy_language:<tag> · reason',
    cta: 'Apply',
    ctaNote: 'Changes what every customer can select.',
    blocked,
    ...(blocked === '' ? {
      submit: (v) => submitWrite(
        () => api.post<unknown>(`${COPY_LANGUAGES_PATH}/${String(v['language'])}`, {
          enabled: v['enabled'] === 'true',
          reason: v['reason'],
        }),
        v['enabled'] === 'true' ? 'The language is offered' : 'The language was withdrawn',
        () => [['language', String(v['language']), '']],
      ),
    } : {}),
  }
}

/** The tags this read reported, for the picker. */
function languagesIn(payload: Record<string, unknown>): readonly string[] {
  const raw = payload['languages']
  if (!Array.isArray(raw)) return []
  const out: string[] = []
  for (const row of raw) {
    if (typeof row === 'string' && row !== '') { out.push(row); continue }
    if (isObject(row)) {
      const tag = row['language'] ?? row['tag'] ?? row['code']
      if (typeof tag === 'string' && tag !== '') out.push(tag)
    }
  }
  return out
}

/**
 * One translation in the workflow, as an option on the step controls.
 *
 * `legal` and `authoredBy` travel with it because the backend's own refusal
 * turns on both: a contractual string cannot be signed off by the person who
 * wrote it, and cannot have been machine-translated at all.
 */
interface WorkflowItem {
  readonly id: string
  readonly label: string
  readonly state: string
  readonly legal: boolean
  readonly authoredBy: string
  readonly authorKind: string
}

function translationsIn(workflow: readonly unknown[]): readonly WorkflowItem[] {
  const out: WorkflowItem[] = []
  for (const raw of workflow) {
    if (!isObject(raw)) continue
    const id = whole(raw['id'])
    if (id === null) continue
    const key = text(raw['copy_key']) || text(raw['key'])
    const state = text(raw['review_state'])
    const legal = raw['legal'] === true
    out.push({
      id: String(id),
      label: `${String(id)} — ${key === '' ? 'key not stated' : key}${state === '' ? '' : ` · ${state}`}${legal ? ' · LEGAL' : ''}`,
      state,
      legal,
      authoredBy: text(raw['authored_by']),
      authorKind: text(raw['author_kind']),
    })
  }
  return out
}

/**
 * Writing a translation of one key.
 *
 * # `author_kind` is asked, never assumed
 *
 * The store refuses anything but `human` or `machine`, and says why: FR-20.6
 * turns on the difference, so it is stated rather than assumed. A contractual
 * string cannot be machine-translated at all — and that is re-checked at
 * PUBLISH time as well as here, because a draft written while the key was
 * product copy legitimately carries `machine`, and the release that reclassified
 * the key is what makes publishing it a breach.
 *
 * So the field has no default and the note carries the consequence. The console
 * cannot know which keys are contractual on this read, so it does not guess —
 * the backend refuses with a message naming the key, which is a better answer
 * than a guess that silently permits.
 *
 * # The source anchor is not a field, and could not be
 *
 * The backend reads the authoritative sentence inside its own transaction and
 * hashes it there, so a translation records the text that actually existed
 * rather than the text a caller says existed. A console that could send its own
 * digest could send one matching whatever it wanted, and every later staleness
 * assessment would agree with it.
 */
function draftTranslationForm(api: ApiClient, caller: Caller, language: string): FormBlock {
  const blocked = writeRefusal(caller, 'copy.write', 'Ops', 'Writing a translation')
  return {
    t: 'form',
    title: `Write a translation — ${language}`,
    lead: 'Drafts one key in this language. Nothing reaches a customer until it is reviewed and published, and the sentence it is translated FROM is anchored by the backend rather than sent from here.',
    fields: [
      {
        name: 'copy_key', label: 'Key', kind: 'text', required: true, mono: true,
        note: 'From the missing list above. It must be a key the running build carries.',
      },
      {
        name: 'body', label: 'Translation', kind: 'textarea', required: true,
        note: 'At most 2000 characters.',
      },
      {
        name: 'author_kind', label: 'Written by', kind: 'select', required: true,
        options: [
          ['human', 'a person'],
          ['machine', 'machine translation'],
        ],
        note: 'Stated rather than assumed. A contractual string cannot be machine-translated, and that is checked again when it is published — a draft written while the key was product copy is not a breach until somebody publishes it after the key was reclassified.',
      },
    ],
    reasons: [
      ['backlog', 'Working the translation backlog', ''],
      ['source_changed', 'The source sentence changed under it', ''],
      ['correction', 'The published translation was wrong', ''],
      ['legal_required', 'Required for a market we serve', ''],
    ],
    reasonNote: 'Recorded against the draft.',
    auditLine: 'admin.copy.translation.draft · actor · copy_translation:<key>/<lang> · reason',
    cta: 'Save draft',
    ctaNote: 'Reaches no customer until it is published.',
    blocked,
    ...(blocked === '' ? {
      submit: (v) => submitWrite(
        () => api.post<unknown>('/copy/translations', {
          copy_key: v['copy_key'],
          language,
          body: v['body'],
          author_kind: v['author_kind'],
          reason: v['reason'],
        }),
        'The translation was drafted',
        (answer) => (isObject(answer) && whole(answer['id']) !== null
          ? [['translation', String(whole(answer['id'])), 'now in the workflow above']]
          : []),
      ),
    } : {}),
  }
}

/**
 * Moving a translation through review.
 *
 * # Two controls rather than three, split where the CONSEQUENCE splits
 *
 * `submit` hands a draft to a reviewer and changes nothing a customer reads.
 * `publish` and `reject` are the review itself. They share a permission here —
 * the copy family has only `copy.write` — so a picker over them widens nothing,
 * and the split is by what the act does rather than by what it needs.
 *
 * # The maker-checker rule is the backend's, and it is stated before the click
 *
 * A contractual string "needs a professional translation and a sign-off from
 * somebody who is not the translator" — the store refuses a publish where the
 * reviewer is the author, for legal keys. This console knows both facts from
 * the workflow read, so it says so on the option rather than letting an
 * operator compose a publish that will be refused. Law 15's two slots are the
 * general form of this; here it is one rule stated in one sentence, and the
 * AML closure pair is where the general control belongs.
 */
function stepTranslationForm(
  api: ApiClient, caller: Caller, items: readonly WorkflowItem[], step: 'submit' | 'decide',
): FormBlock {
  const submitting = step === 'submit'
  const relevant = items.filter((i) => (submitting
    ? i.state === 'draft' || i.state === 'rejected'
    : i.state === 'in_review'))

  const me = caller.session?.subject ?? ''
  const options = relevant.map((i) => {
    // Stated on the option, because an operator picking it needs to know
    // before they compose the rest of the form.
    const ownLegal = !submitting && i.legal && i.authoredBy !== '' && i.authoredBy === me
    const machineLegal = !submitting && i.legal && i.authorKind === 'machine'
    const caveat = ownLegal
      ? ' · you wrote this, and a contractual string needs a different signer'
      : machineLegal
        ? ' · contractual and machine-written, so it cannot be published'
        : ''
    return [i.id, `${i.label}${caveat}`] as const
  })

  const empty = submitting
    ? 'No translation is drafted or rejected, so there is nothing to send for review.'
    : 'No translation is waiting for review.'
  const blocked = relevant.length === 0
    ? empty
    : writeRefusal(
        caller, 'copy.write', 'Ops',
        submitting ? 'Sending a translation for review' : 'Publishing or rejecting a translation',
      )

  return {
    t: 'form',
    title: submitting ? 'Send a translation for review' : 'Publish or reject a translation',
    lead: submitting
      ? 'Hands a draft to a reviewer. Nothing a customer reads changes here.'
      : 'Publishing puts this translation in front of customers. A contractual string cannot be signed off by the person who wrote it, and cannot have been machine-translated.',
    fields: [
      {
        name: 'id', label: 'Translation', kind: 'select', required: true,
        options,
      },
      ...(submitting ? [] : [{
        name: 'action', label: 'Action', kind: 'select' as const, required: true,
        options: [
          ['publish', 'publish — customers read this'],
          ['reject', 'reject — send it back to the translator'],
        ] as const,
        note: 'Required, and there is no default.',
      }]),
    ],
    reasons: submitting
      ? [
          ['ready_for_review', 'The draft is finished', ''],
          ['redrafted', 'Rewritten after a rejection', ''],
        ]
      : [
          ['review_passed', 'Reviewed and correct', 'publish'],
          ['professional_signoff', 'Professionally translated and signed off', 'publish'],
          ['review_failed', 'Sent back to the translator', 'reject'],
          ['meaning_wrong', 'The translation changes the meaning', 'reject'],
          ['machine_on_legal', 'A contractual string that was machine-translated', 'reject'],
        ],
    reasonNote: 'Recorded against the translation.',
    auditLine: `admin.copy.translation.${submitting ? 'submit' : '<action>'} · actor · copy_translation:<id> · reason`,
    cta: submitting ? 'Send for review' : 'Apply',
    ctaNote: submitting
      ? 'Nothing on the customer surface changes.'
      : 'Publishing takes effect for customers immediately.',
    blocked,
    ...(blocked === '' ? {
      submit: (v) => submitWrite(
        () => api.post<unknown>(
          `/copy/translations/${String(v['id'])}/${submitting ? 'submit' : String(v['action'])}`,
          submitting ? { reason: v['reason'] } : { reason_code: v['reason'], reason: v['reason'] },
        ),
        submitting ? 'Sent for review' : `The translation was ${String(v['action'])}ed`,
        (answer) => (isObject(answer)
          ? [['state', text(answer['review_state']), 'as the backend recorded it']]
          : []),
      ),
    } : {}),
  }
}
